import {
  type IAdminWithdrawalDetail,
  type IAdminWithdrawalListItem,
  type IAdminWithdrawalOrderCandidate,
} from '@mahawi/eshop-common/src/types';
import { createSlice } from '@reduxjs/toolkit';

import {
  asyncStateInitial,
  finishLoading,
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
  startLoading,
} from '../async-state';
import {
  type IWithdrawalCancelOrderInput,
  type IWithdrawalCreateInput,
  type IWithdrawalFailure,
  type IWithdrawalSetStatusInput,
  type IWithdrawalsFilter,
  type IWithdrawalState,
} from './types';

export const preloadedState: IWithdrawalState = {
  ...asyncStateInitial,
  list: undefined,
  detail: undefined,
  byOrder: undefined,
  orderSearchResults: undefined,
  receivedCount: 0,
  createdUuid: null,
  failure: null,
};

const withdrawalSlice = createSlice({
  name: 'Withdrawal',
  initialState: preloadedState,
  reducers: {
    withdrawalsLoad: {
      reducer(state) {
        startLoading(state);
      },
      prepare(filter: IWithdrawalsFilter) {
        return { payload: filter };
      },
    },
    withdrawalsLoadResponse: {
      reducer(
        state,
        action: { payload: { withdrawals: IAdminWithdrawalListItem[] } },
      ) {
        state.list = action.payload.withdrawals;
        finishLoading(state);
      },
      prepare(withdrawals: IAdminWithdrawalListItem[]) {
        return { payload: { withdrawals } };
      },
    },
    withdrawalLoad: {
      reducer(state) {
        startLoading(state);
        state.failure = null;
      },
      prepare(uuid: string) {
        return { payload: { uuid } };
      },
    },
    withdrawalLoadResponse: {
      reducer(
        state,
        action: { payload: { detail: IAdminWithdrawalDetail | null } },
      ) {
        state.detail = action.payload.detail;
        finishLoading(state);
      },
      prepare(detail: IAdminWithdrawalDetail | null) {
        return { payload: { detail } };
      },
    },
    withdrawalOrderSearch: {
      reducer(state) {
        startLoading(state);
      },
      prepare(query: string) {
        return { payload: { query } };
      },
    },
    withdrawalOrderSearchResponse: {
      reducer(
        state,
        action: { payload: { orders: IAdminWithdrawalOrderCandidate[] } },
      ) {
        state.orderSearchResults = action.payload.orders;
        finishLoading(state);
      },
      prepare(orders: IAdminWithdrawalOrderCandidate[]) {
        return { payload: { orders } };
      },
    },
    withdrawalLinkOrder: {
      reducer(state) {
        startLoading(state);
        state.failure = null;
      },
      prepare(uuid: string, orderUuid: string | null) {
        return { payload: { uuid, orderUuid } };
      },
    },
    withdrawalSetStatus: {
      reducer(state) {
        startLoading(state);
        state.failure = null;
      },
      prepare(input: IWithdrawalSetStatusInput) {
        return { payload: input };
      },
    },
    withdrawalCancelOrder: {
      reducer(state) {
        startLoading(state);
        state.failure = null;
      },
      prepare(input: IWithdrawalCancelOrderInput) {
        return { payload: input };
      },
    },
    withdrawalMutationResponse: {
      reducer(
        state,
        action: {
          payload: {
            detail: IAdminWithdrawalDetail | null;
            failure: IWithdrawalFailure | null;
          };
        },
      ) {
        if (action.payload.detail) {
          state.detail = action.payload.detail;
        }

        state.failure = action.payload.failure;
        finishLoading(state);
      },
      prepare(
        detail: IAdminWithdrawalDetail | null,
        failure: IWithdrawalFailure | null,
      ) {
        return { payload: { detail, failure } };
      },
    },
    withdrawalCreate: {
      reducer(state) {
        startLoading(state);
        state.createdUuid = null;
        state.failure = null;
      },
      prepare(input: IWithdrawalCreateInput) {
        return { payload: input };
      },
    },
    withdrawalCreateResponse: {
      reducer(
        state,
        action: {
          payload: { uuid: string | null; failure: IWithdrawalFailure | null };
        },
      ) {
        state.createdUuid = action.payload.uuid;
        state.failure = action.payload.failure;
        finishLoading(state);
      },
      prepare(uuid: string | null, failure: IWithdrawalFailure | null) {
        return { payload: { uuid, failure } };
      },
    },
    withdrawalCreateReset(state) {
      state.createdUuid = null;
      state.failure = null;
    },
    withdrawalReceivedCount(state) {
      state.error = undefined;
    },
    withdrawalReceivedCountResponse: {
      reducer(state, action: { payload: { count: number } }) {
        state.receivedCount = action.payload.count;
      },
      prepare(count: number) {
        return { payload: { count } };
      },
    },
    withdrawalsLoadByOrder: {
      reducer(state) {
        state.byOrder = undefined;
      },
      prepare(orderUuid: string) {
        return { payload: { orderUuid } };
      },
    },
    withdrawalsLoadByOrderResponse: {
      reducer(
        state,
        action: { payload: { withdrawals: IAdminWithdrawalListItem[] } },
      ) {
        state.byOrder = action.payload.withdrawals;
        state.updatedAt = Date.now();
      },
      prepare(withdrawals: IAdminWithdrawalListItem[]) {
        return { payload: { withdrawals } };
      },
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      requestFailedMatcher('Withdrawal'),
      resetLoadingOnRequestFailed,
    );
  },
});

export const {
  withdrawalCancelOrder,
  withdrawalCreate,
  withdrawalCreateReset,
  withdrawalCreateResponse,
  withdrawalLinkOrder,
  withdrawalLoad,
  withdrawalLoadResponse,
  withdrawalMutationResponse,
  withdrawalOrderSearch,
  withdrawalOrderSearchResponse,
  withdrawalReceivedCount,
  withdrawalReceivedCountResponse,
  withdrawalSetStatus,
  withdrawalsLoad,
  withdrawalsLoadByOrder,
  withdrawalsLoadByOrderResponse,
  withdrawalsLoadResponse,
} = withdrawalSlice.actions;
export default withdrawalSlice.reducer;
